import { useState, useEffect, useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';

// A localStorage-backed list of RECORDS (objects with an id), as opposed to
// useOptionList which holds a list of strings.
//
// Sunday School's academic years, classes, teachers, weekly registers, exams
// and marks are all stored this way — they have no backend endpoint, so they
// live only in the browser that entered them. See the note in SundaySchool.jsx.

/** Records held in localStorage always carry a client-generated string id. */
export interface LocalRecord {
  id: string;
}

export interface UseLocalCollectionResult<T extends LocalRecord> {
  items: T[];
  setItems: Dispatch<SetStateAction<T[]>>;
  add: (record: T) => T;
  update: (id: string, changes: Partial<T>) => void;
  remove: (id: string) => void;
  upsertBy: (matcher: (item: T) => boolean, record: T) => void;
}

export const useLocalCollection = <T extends LocalRecord = LocalRecord>(
  storageKey: string,
  initial: T[] = []
): UseLocalCollectionResult<T> => {
  const [items, setItems] = useState<T[]>(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (Array.isArray(stored)) return stored as T[];
    } catch { /* fall through */ }
    return initial;
  });

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(items));
  }, [storageKey, items]);

  // Newest first, matching how these lists are displayed.
  const add = useCallback((record: T) => {
    setItems((prev) => [record, ...prev]);
    return record;
  }, []);

  const update = useCallback((id: string, changes: Partial<T>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...changes } : it)));
  }, []);

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  // Replace any existing record for the same key, then prepend the new one.
  const upsertBy = useCallback((matcher: (item: T) => boolean, record: T) => {
    setItems((prev) => [record, ...prev.filter((it) => !matcher(it))]);
  }, []);

  return { items, setItems, add, update, remove, upsertBy };
};

export default useLocalCollection;
